import { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Star, ArrowUpRight, BadgeCheck, ArrowLeft } from "lucide-react";
import Seo from "@/components/Seo";
import ReviewCard from "@/components/ReviewCard";
import { REVIEWS, AGGREGATE, SOURCES, GOOGLE_URL } from "@/data/reviews";
import { useLiveStats } from "@/lib/liveStats";
import { SITE } from "@/data/site";
import { localBusinessLd, websiteLd, breadcrumbLd } from "@/lib/jsonld";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Stars = ({ size = "w-3.5 h-3.5" }) => (
  <span className="inline-flex items-center gap-0.5" aria-label="5 out of 5 stars">
    {[...Array(5)].map((_, i) => (
      <Star key={i} className={`${size} fill-[#B8923A] text-[#B8923A]`} strokeWidth={1} />
    ))}
  </span>
);

const SOURCE_LIST = ["All", "Hitched", "Bridebook", "Google", "Facebook", "Guides for Brides"];

export default function AllReviews() {
  const [filter, setFilter] = useState("All");
  const stats = useLiveStats();
  const [googleLive, setGoogleLive] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/google-reviews`)
      .then((r) => setGoogleLive(r.data))
      .catch(() => setGoogleLive(null));
  }, []);

  // Merge live Google reviews (if available) into the hardcoded set
  const allReviews = useMemo(() => {
    if (!googleLive?.reviews?.length) return REVIEWS;
    const liveGoogle = googleLive.reviews
      .filter((g) => g.text)
      .map((g, i) => ({
        name: g.authorName || "Google reviewer",
        date: g.relativePublishTime || "Recent",
        title: (g.text || "").split(/[.!?]/)[0].slice(0, 60) || "Five-star Google review",
        text: g.text,
        source: "Google",
        url: g.authorUrl || GOOGLE_URL,
        iso: g.publishTime || "9999-01-01",
        rating: g.rating || 5,
      }));
    return [...liveGoogle, ...REVIEWS].sort((a, b) => (a.iso < b.iso ? 1 : -1));
  }, [googleLive]);

  const filtered = useMemo(() => {
    if (filter === "All") return allReviews;
    return allReviews.filter((r) => r.source === filter);
  }, [filter, allReviews]);

  return (
    <>
      <Seo
        title={`All Reviews | ${stats.total} Five-Star Reviews | Wedding Video Essex`}
        description={`Every verified review for Wedding Video Essex, sorted by date. ${stats.total} five-star reviews across Hitched, Google, Facebook, Bridebook and Guides for Brides. 100% recommended. Click any review to verify on its source.`}
        canonical={`${SITE.url}/reviews`}
        ogImage="https://customer-assets.emergentagent.com/job_wve-wedding-showcase/artifacts/4tj3d52l_Helen%20%26%20Daniel%2014-07-25%20%20009.jpg"
        jsonLd={[
          websiteLd(),
          localBusinessLd(),
          breadcrumbLd([
            { name: "Home", url: SITE.url },
            { name: "All reviews", url: `${SITE.url}/reviews` },
          ]),
        ]}
      />

      {/* Header */}
      <section className="py-20 md:py-32 bg-stone-50 relative">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10">
          <Link
            to="/"
            data-testid="reviews-back-home"
            className="inline-flex items-center gap-2 font-serif italic text-stone-500 hover:text-stone-900 transition-colors mb-10"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </Link>

          <p className="overline mb-7">Every review. Every source.</p>
          <h1 className="font-serif text-[2.6rem] sm:text-[3.4rem] md:text-[4.8rem] leading-[1.04] tracking-tight text-stone-900 max-w-4xl">
            All <em className="italic font-normal text-stone-600">{stats.total} reviews</em>, in their own words.
          </h1>

          <div className="mt-10 flex flex-col md:flex-row items-start md:items-center gap-5 md:gap-10">
            <div className="flex items-center gap-3">
              <Stars size="w-4 h-4" />
              <p className="font-serif text-stone-900 text-lg">
                <span className="kbd text-xl">{stats.rating.toFixed(1)}</span>
                <span className="italic text-stone-500 ml-2">across all platforms</span>
              </p>
            </div>
            <p className="font-serif italic text-stone-600">
              100% of {stats.total} couples recommend us.
            </p>
          </div>

          {/* Source totals */}
          <h2 className="font-serif italic text-stone-900 text-2xl md:text-3xl mt-14 mb-6">
            Reviews by source platform
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-8">
            {Object.entries(SOURCES).map(([key, s]) => (
              <a
                key={key}
                href={s.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`reviews-source-link-${key.toLowerCase()}`}
                className="group block"
              >
                <p className="font-serif text-stone-900 text-[2rem] md:text-[2.4rem] leading-none kbd">
                  {key === 'Google' ? stats.googleCount : (s.totalReviews || '—')}
                </p>
                <p className="mt-2 font-serif italic text-stone-500 group-hover:text-stone-900 transition-colors text-base inline-flex items-center gap-1.5">
                  on {s.label}
                  <ArrowUpRight className="w-3.5 h-3.5" />
                </p>
              </a>
            ))}
          </div>

          {/* Filter chips */}
          <div className="mt-12 flex flex-wrap items-center gap-3">
            <span className="overline mr-2">Filter</span>
            {SOURCE_LIST.map((s) => {
              const active = filter === s;
              return (
                <button
                  key={s}
                  onClick={() => setFilter(s)}
                  data-testid={`reviews-filter-${s.toLowerCase()}`}
                  className={`px-5 py-2 font-serif italic text-base rounded-full border transition-all ${
                    active
                      ? "bg-stone-900 text-stone-50 border-stone-900"
                      : "border-stone-300 text-stone-700 hover:border-stone-900"
                  }`}
                >
                  {s}
                  {s !== "All" && (
                    <span className="ml-2 text-xs not-italic kbd opacity-70">
                      {allReviews.filter((r) => r.source === s).length}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Review list */}
      <section className="pb-32 bg-stone-50" data-testid="all-reviews-list">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10">
          <h2 className="font-serif text-stone-900 text-3xl md:text-4xl mb-10 mt-4">
            Every five-star review, in chronological order
          </h2>
        {/* Review list */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {filtered.map((r, i) => (
              <ReviewCard
                key={`${r.name}-${r.iso}-${i}`}
                r={r}
                testId={`all-review-${i}`}
                sourceTestId={`all-review-source-${i}`}
              />
            ))}
          </div>

          {/* External CTAs */}
          <div className="mt-24 pt-16 border-t border-stone-200">
            <p className="overline text-center mb-10">Read more on the source platforms</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-10">
              {Object.entries(SOURCES).map(([key, s]) => (
                <a
                  key={key}
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`reviews-cta-${key.toLowerCase()}`}
                  className="group block text-center"
                >
                  <p className="font-serif italic text-stone-700 group-hover:text-stone-900 text-lg inline-flex items-center gap-2 border-b border-stone-300 group-hover:border-stone-700 pb-1 transition-colors">
                    Read on {s.label}
                    <ArrowUpRight className="w-4 h-4" />
                  </p>
                </a>
              ))}
            </div>
          </div>

          {/* Back to home */}
          <div className="mt-20 text-center">
            <Link
              to="/"
              data-testid="reviews-bottom-back"
              className="btn-primary"
            >
              Back to home
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
